import React, { useState, useEffect } from 'react';
import { Form, Button, Card, Row, Col, Image, Alert } from 'react-bootstrap';
import api from '../../utils/api';

const HeroManager = () => {
  const API_URL = process.env.REACT_APP_API_URL;
  const [settings, setSettings] = useState({
    mainHeading: '',
    subHeading: '',
    images: [],
  });
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);
  const [newImage, setNewImage] = useState(null);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const { data } = await api.get('/hero');
      setSettings(data);
    } catch (err) {
      console.error(err);
    }
  };

  const handleSaveText = async (e) => {
    e.preventDefault();
    setMessage(null);
    setError(null);
    try {
      await api.put('/hero/text', {
        mainHeading: settings.mainHeading,
        subHeading: settings.subHeading,
      });
      setMessage('Text updated successfully!');
    } catch (err) {
      setError('Failed to update text.');
    }
  };

  const handleUploadImage = async (e) => {
    e.preventDefault();
    if (!newImage) return;

    const formData = new FormData();
    formData.append('image', newImage);

    try {
      await api.post('/hero/image', formData);
      setNewImage(null);
      // Reset file input
      document.getElementById('heroImageInput').value = '';
      fetchSettings(); // Refresh list
    } catch (err) {
      alert('Failed to upload image');
    }
  };

  const handleDeleteImage = async (imagePath) => {
    if (window.confirm('Are you sure you want to remove this image?')) {
      try {
        await api.delete('/hero/image', { data: { imagePath } });
        fetchSettings();
      } catch (err) {
        alert('Failed to delete image');
      }
    }
  };

  const handleMoveImage = (index, direction) => {
    const newImages = [...settings.images];
    const targetIndex = index + direction;

    if (targetIndex < 0 || targetIndex >= newImages.length) return;

    // Swap
    [newImages[index], newImages[targetIndex]] = [newImages[targetIndex], newImages[index]];
    setSettings({ ...settings, images: newImages });
  };

  const handleSaveOrder = async () => {
    setMessage(null);
    setError(null);
    try {
      await api.put('/hero/images', { images: settings.images });
      setMessage('Image order saved successfully!');
    } catch (err) {
      setError('Failed to save image order.');
    }
  };

  return (
    <div>
      <h3 className="mb-4">Hero Section Manager</h3>
      {message && <Alert variant="success">{message}</Alert>}
      {error && <Alert variant="danger">{error}</Alert>}

      {/* Text Settings */}
      <Card className="mb-4">
        <Card.Body>
          <h5 className="mb-3">Headings</h5>
          <Form onSubmit={handleSaveText}>
            <Form.Group className="mb-3">
              <Form.Label>Main Heading</Form.Label>
              <Form.Control 
                type="text" 
                value={settings.mainHeading} 
                onChange={(e) => setSettings({ ...settings, mainHeading: e.target.value })}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Sub Heading</Form.Label>
              <Form.Control 
                type="text" 
                value={settings.subHeading} 
                onChange={(e) => setSettings({ ...settings, subHeading: e.target.value })}
              />
            </Form.Group>
            <Button variant="primary" type="submit">Update Text</Button>
          </Form>
        </Card.Body>
      </Card>

      {/* Image Settings */}
      <Card>
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="mb-0">Slider Images</h5>
            <Button variant="info" onClick={handleSaveOrder} disabled={settings.images.length === 0}>
              Save Order
            </Button>
          </div>
          
          <Row className="g-3 mb-4">
            {settings.images.length === 0 ? (
              <Col><p className="text-muted">No custom images uploaded. Using defaults (if any).</p></Col>
            ) : (
              settings.images.map((img, idx) => (
                <Col key={idx} xs={6} md={4} lg={3}>
                  <div className="position-relative border p-2 rounded">
                    <Image 
                      src={`${API_URL}${img}`} 
                      thumbnail 
                      style={{ width: '100%', height: '150px', objectFit: 'cover' }}
                      className="mb-2"
                    />
                    <div className="d-flex justify-content-between">
                      <Button 
                        variant="secondary" 
                        size="sm"
                        disabled={idx === 0}
                        onClick={() => handleMoveImage(idx, -1)}
                      >
                        &lt;
                      </Button>
                      <Button 
                        variant="danger" 
                        size="sm" 
                        onClick={() => handleDeleteImage(img)}
                      >
                        Delete
                      </Button>
                      <Button 
                        variant="secondary" 
                        size="sm"
                        disabled={idx === settings.images.length - 1}
                        onClick={() => handleMoveImage(idx, 1)}
                      >
                        &gt;
                      </Button>
                    </div>
                  </div>
                </Col>
              ))
            )}
          </Row>

          <hr />
          
          <h6 className="mb-3">Add New Image</h6>
          <Form onSubmit={handleUploadImage} className="d-flex gap-2">
            <Form.Control 
              type="file" 
              id="heroImageInput"
              onChange={(e) => setNewImage(e.target.files[0])}
            />
            <Button variant="success" type="submit" disabled={!newImage}>Upload</Button>
          </Form>
        </Card.Body>
      </Card>
    </div>
  );
};

export default HeroManager;
